"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { AnimatePresence, motion } from "motion/react";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { SendParcelButton } from "./send/SendParcelButton";
import { LanguageToggle } from "./LanguageToggle";

/**
 * Deux publics, deux navigations.
 *
 * Un particulier qui envoie un colis à sa famille et une agence qui cherche
 * un partenaire logistique ne veulent pas les mêmes pages. Le sélecteur les
 * sépare, comme Monzo sépare Personal et Business : chacun ne voit que ce qui
 * le concerne, au lieu d'une barre où tout coexiste et où personne ne
 * reconnaît sa moitié.
 *
 * ⚠️ Le mode n'est PAS un état mémorisé, c'est la section où l'on se trouve.
 * Une préférence stockée aurait demandé de la lire après le montage — donc un
 * premier rendu dans le mauvais mode, et une barre qui change sous les yeux du
 * visiteur. Ici, cliquer sur un onglet est une navigation, et l'URL suffit à
 * dire dans quel monde on est. Les pages neutres (aide, mentions légales)
 * retombent sur « particulier », qui est le trafic principal.
 */
const PARTICULIER = [
  { href: "/", key: "home" },
  { href: "/suivi", key: "tracking" },
  { href: "/partenaires", key: "partners" },
] as const;

const ENTREPRISE = [
  // « Accueil » et non « Entreprise » : l'onglet actif du sélecteur dit déjà
  // dans quelle moitié du site on est, et le mot apparaissait deux fois de
  // suite. Chaque monde a donc son accueil, au même endroit.
  { href: "/entreprise", key: "home" },
  { href: "/tarifs", key: "pricing" },
] as const;

/** Les chemins qui appartiennent au monde professionnel. */
const CHEMINS_ENTREPRISE = ENTREPRISE.map((l) => l.href);

function Logo() {
  return (
    <Link href="/" className="flex items-center no-underline" aria-label="Expedys">
      <Image
        src="/EXPEDYS_LOGO1.png"
        alt="Expedys"
        width={362}
        height={100}
        priority
        className="h-[26px] w-auto max-[880px]:h-6"
      />
    </Link>
  );
}

/**
 * Le sélecteur de public : deux onglets qui sont de vrais liens.
 *
 * De vrais liens, et pas des boutons : le visiteur peut les ouvrir dans un
 * onglet, les mettre en favori, et un moteur de recherche suit les deux
 * moitiés du site.
 */
function SelecteurPublic({ entreprise }: { entreprise: boolean }) {
  const t = useTranslations("nav");

  const onglet = (actif: boolean) =>
    cn(
      "rounded-full px-3.5 py-1.5 text-[14px] no-underline transition max-[880px]:px-3 max-[880px]:text-[13px]",
      actif
        ? "bg-white font-bold text-espresso shadow-[0_1px_3px_rgba(28,16,10,.16)]"
        : "font-medium text-ink hover:text-espresso",
    );

  return (
    <div className="flex items-center gap-1 rounded-full bg-cream p-1">
      <Link href="/" className={onglet(!entreprise)}>
        {t("personal")}
      </Link>
      <Link href="/entreprise" className={onglet(entreprise)}>
        {t("enterprise")}
      </Link>
    </div>
  );
}

export function Navbar() {
  const t = useTranslations("nav");
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const entreprise = CHEMINS_ENTREPRISE.some((href) => pathname.startsWith(href));
  const liens = entreprise ? ENTREPRISE : PARTICULIER;

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-white/95">
      <div className="mx-auto flex max-w-container items-center gap-5 px-7 py-[15px] max-[880px]:gap-2.5 max-[880px]:px-[18px] max-[880px]:py-3.5">
        <Logo />
        <div className="max-[880px]:hidden">
          <SelecteurPublic entreprise={entreprise} />
        </div>

        {/* Nav desktop */}
        <nav className="ml-auto flex gap-[26px] max-[880px]:hidden">
          {liens.map(({ href, key }) => (
            <Link
              key={href}
              href={href}
              className={cn(
                "text-[15px] no-underline transition-colors hover:text-terracotta",
                isActive(href)
                  ? "font-bold text-terracotta"
                  : "font-medium text-espresso-700",
              )}
            >
              {t(key)}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3 min-[881px]:ml-0">
          <LanguageToggle />
          {/* « Envoyer un colis » n'a rien à faire côté entreprise : une
              agence ne dépose pas un colis, elle prend rendez-vous. */}
          {entreprise ? null : (
            <div className="max-[880px]:hidden">
              <SendParcelButton variant="compact" />
            </div>
          )}

          {/* Burger */}
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={t("menu")}
            aria-expanded={open}
            className="hidden h-[42px] w-[42px] items-center justify-center rounded-[11px] border border-[#DDD1C2] bg-white max-[880px]:flex"
          >
            <span className="relative block h-[14px] w-[18px]">
              <motion.span
                className="absolute left-0 h-0.5 w-[18px] rounded bg-espresso"
                animate={open ? { top: 6, rotate: 45 } : { top: 0, rotate: 0 }}
                transition={{ duration: 0.3 }}
              />
              <motion.span
                className="absolute left-0 top-[6px] h-0.5 w-[18px] rounded bg-espresso"
                animate={{ opacity: open ? 0 : 1 }}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="absolute left-0 h-0.5 w-[18px] rounded bg-espresso"
                animate={open ? { top: 6, rotate: -45 } : { top: 12, rotate: 0 }}
                transition={{ duration: 0.3 }}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Tiroir mobile */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="overflow-hidden border-t border-line bg-white/98 min-[881px]:hidden"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.38, ease: [0.3, 0.8, 0.3, 1] }}
          >
            <div className="flex flex-col px-5 pb-5 pt-2.5">
              {/* Le sélecteur ne tient pas dans l'en-tête d'un téléphone :
                  à 375 px, il poussait la langue et le menu hors de l'écran.
                  Il ouvre donc le tiroir, où il a la place de se lire. */}
              <div className="mb-3 flex justify-center border-b border-[#EADFD1] pb-4">
                <SelecteurPublic entreprise={entreprise} />
              </div>
              {liens.map(({ href, key }, i) => (
                <Link
                  key={href}
                  href={href}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "rounded-lg px-2 py-[15px] text-lg no-underline active:bg-[#F0E2D5]",
                    i < liens.length - 1 && "border-b border-[#EADFD1]",
                    isActive(href)
                      ? "font-bold text-terracotta"
                      : "font-semibold text-espresso-700",
                  )}
                >
                  {t(key)}
                </Link>
              ))}
              {entreprise ? null : (
                <SendParcelButton variant="block" className="mt-4 rounded-full" />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
